import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

const CachingSystem = ({ testMode = false }) => {
  const host = config.backendUrl;

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setAiResponse('');
    setError(null);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setAiResponse('');
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!imageFile) {
      setError('Please upload a dog photo first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('Not authenticated.');

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(`${host}/api/admin/dog-characteristics`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Request failed (HTTP ${response.status})`);
      }

      const data = await response.json();
      setAiResponse(data.characteristics || '');
    } catch (err) {
      console.error('Dog characteristics analysis failed:', err);
      setError(err.message || 'Something went wrong analyzing the image.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            Caching System
            {testMode && <span style={{ color: 'var(--warning-color)' }}> (Test Mode)</span>}
          </h1>
        </div>
        <div className="page-header-actions">
          <Link to={testMode ? '/test' : '/'} className="btn btn-secondary">
            ← Back Home
          </Link>
        </div>
      </div>

      {/* Upload */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Upload Dog Photo</h3>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label" htmlFor="dog-image-input">
              Dog photo
            </label>
            <input
              id="dog-image-input"
              type="file"
              accept="image/*"
              className="form-input"
              onChange={handleImageChange}
              disabled={loading}
            />
          </div>

          {imagePreview && (
            <div style={{ marginTop: 'var(--spacing-md)' }}>
              <img
                src={imagePreview}
                alt={imageFile ? imageFile.name : 'Uploaded dog'}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-medium)',
                }}
              />
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-md)' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAnalyze}
                  disabled={loading}
                >
                  {loading ? 'Analyzing…' : 'Analyze'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={clearImage}
                  disabled={loading}
                >
                  Remove image
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="form-error" style={{ marginTop: 'var(--spacing-md)' }}>
              {error}
            </p>
          )}
        </div>
      </div>

      {/* AI Response */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">AI Response</h3>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label" htmlFor="ai-response-box">
              Generated output
            </label>
            <textarea
              id="ai-response-box"
              className="form-input"
              rows={10}
              placeholder="AI-generated analysis and image results will appear here…"
              value={aiResponse}
              onChange={(e) => setAiResponse(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CachingSystem;
